import { useEffect, useState, useRef } from "react";
import { InputNumber, Radio, Select, Space } from "antd";
import {
  ITextChangeMod,
  findLayersOrLayerAssets,
  getPath,
  COMMON_PATHS,
} from "@blings/blings-player";
import {
  VerticalAlignBottomOutlined,
  VerticalAlignMiddleOutlined,
  VerticalAlignTopOutlined,
} from "@ant-design/icons";
import { ModsPanelCompProps } from "../ModsPanel";
import { DynamicValueMod, DynamicValueModTypes } from "../DynamicValueMod";
import { InfoTooltip } from "../../../BaseComps/InfoTooltip";
import { LayerSelector } from "../../LayerSelector";
import { rootStore } from "../../../stores/main";
import { ExperimentOptimizationTechnique } from "../../../API";
import { buildTextLayerOption } from "../../../helpers/layerSelectorFilters";

const DEFAULT_MAX_CHARS = undefined;

const { Option } = Select;
export function TextChangeForm(props: ModsPanelCompProps<ITextChangeMod>) {
  const { change, json, index, onChange } = props;
  const [valueSetterType, setValueSetterType] = useState("dataKey");
  // prevSetterType - Save the previous setter type and its font size and maxChars to be used
  // when switching between setter types - temporary solution
  const prevSetterType = useRef({
    setterType: "" as string,
    dataKeyMaxChars: (change.maxChars ?? DEFAULT_MAX_CHARS) as
      | string
      | number
      | undefined,
    valueFontSize: change.fontSize as string | number | undefined,
  });

  const hasExperimentsEnabled =
    rootStore.experimentStore.selectedExperimentOptimizationTechnique &&
    rootStore.experimentStore.selectedExperimentOptimizationTechnique !==
      ExperimentOptimizationTechnique.DISABLED;
  const experimentId = change.experimentId as string;
  const experimentData =
    experimentId !== undefined
      ? rootStore.experimentStore.getExperiment(experimentId)
      : undefined;
  const experimentVariants =
    experimentId !== undefined ? experimentData?.variants : undefined;
  const hasExperiment =
    experimentId !== undefined &&
    experimentVariants &&
    experimentVariants.length >= 2;

  // set fontSize
  useEffect(() => {
    const prevSetter = { ...prevSetterType.current };
    // If the setter type has changed, save the font size and maxChars of the previous setter type
    // since the font size and maxChars are shared between the setter types and they are being reset
    prevSetterType.current = {
      setterType: valueSetterType,
      dataKeyMaxChars:
        valueSetterType === "dataKey" ||
        (hasExperimentsEnabled && hasExperiment)
          ? change.maxChars ?? prevSetter.dataKeyMaxChars
          : prevSetter.dataKeyMaxChars,
      valueFontSize:
        valueSetterType === "value"
          ? change.fontSize ?? prevSetter.valueFontSize
          : prevSetter.valueFontSize,
    };

    if (!change.fontSize && valueSetterType === "value") {
      // Get font size from json
      const layers = findLayersOrLayerAssets({
        assetId: change["assetId"],
        layerName: change["layerName"],
        layerUid: change["layerUid"],
        jsonVid: json,
      });
      if (layers?.length) {
        const layer = layers[0];
        const fontSize: string =
          prevSetter.valueFontSize ??
          getPath(layer, COMMON_PATHS.FONTSIZE_IN_TEXT_LAYER); // If the layer has text
        onChange(index, "fontSize", fontSize);
      }
    }
    // avoid reseting fontsize when open the mod for the first time
    if (valueSetterType === "dataKey" && prevSetter.setterType !== "") {
      onChange(index, "fontSize", undefined);
    }
    if (valueSetterType === "dataKey") {
      onChange(
        index,
        "maxChars",
        change.maxChars ?? prevSetter.dataKeyMaxChars
      );
    }
    // chaning the fontSize to maxChars if the experiment is enabled
    if (valueSetterType === "value" && hasExperimentsEnabled && hasExperiment) {
      onChange(
        index,
        "fontSize",
        change.maxChars ?? prevSetter.dataKeyMaxChars
      );
    }
  }, [
    valueSetterType,
    change,
    json,
    index,
    onChange,
    change.maxChars,
    change.fontSize,
    hasExperiment,
    hasExperimentsEnabled,
  ]);

  const templateClearedRefs = useRef({
    fontSize: false,
    maxChars: false,
  });

  useEffect(() => {
    if (valueSetterType !== "value") {
      templateClearedRefs.current.fontSize = false;
      templateClearedRefs.current.maxChars = false;
      return;
    }
    const hasTemplate = /\$\{.*?\}/.test(change?.value || "");
    if (hasTemplate) {
      templateClearedRefs.current.maxChars = false;
      if (
        typeof change.fontSize !== "undefined" &&
        !templateClearedRefs.current.fontSize
      ) {
        templateClearedRefs.current.fontSize = true;
        onChange(index, "fontSize", undefined);
      }
    } else {
      templateClearedRefs.current.fontSize = false;
      if (
        typeof change.maxChars !== "undefined" &&
        !templateClearedRefs.current.maxChars
      ) {
        templateClearedRefs.current.maxChars = true;
        onChange(index, "maxChars", undefined);
      }
    }
  }, [
    change.value,
    change.fontSize,
    change.maxChars,
    valueSetterType,
    index,
    onChange,
  ]);

  enum CaseAdjustments {
    NoChange = "",
    Uppercase = "uppercase",
    Lowercase = "lowercase",
    Titlecase = "titlecase",
    Sentencecase = "sentencecase",
  }
  const getExplanation = (value: CaseAdjustments) => {
    switch (value) {
      case CaseAdjustments.Sentencecase:
        return "Capitalizes first letter of a sentence";
      case CaseAdjustments.Titlecase:
        return "Capitalizes First Letter In Each Word";
      case CaseAdjustments.Lowercase:
        return "converts text to lowercase";
      case CaseAdjustments.Uppercase:
        return "CONVERTS TEXT TO UPPERCASE";
      case CaseAdjustments.NoChange:
      default:
        return "No change";
    }
  };
  const verticalAlignmentInput = (
    <div className="mod-extra-data-container">
      <div className="name-info-container">
        <span>Vertical Alignment</span>
        <InfoTooltip
          info={"Centers text vertically within its bounding box."}
        />
      </div>
      <Radio.Group
        className="vertical-align-radio"
        value={change.verticalAlign || "top"}
        style={{ width: 120 }}
        onChange={(e) => {
          props.onChange(props.index, "verticalAlign", e.target.value);
        }}
        optionType="button"
        buttonStyle="solid"
      >
        <Radio value="top">
          <VerticalAlignTopOutlined />
        </Radio>
        <Radio value="center">
          <VerticalAlignMiddleOutlined />
        </Radio>
        <Radio value="bottom">
          <VerticalAlignBottomOutlined />
        </Radio>
      </Radio.Group>
    </div>
  );
  const fontSizeOrBreakPointInput = () => {
    const isDynamic =
      ["dataKey", "expression", "liveControlKey", "inputName"].includes(
        valueSetterType
      ) ||
      (valueSetterType === "value" && /\$\{.*?\}/.test(change.value || "")) ||
      (hasExperimentsEnabled && hasExperiment);

    const title = isDynamic ? "Resize Trigger " : "Font-size ";
    const infoTooltipText =
      "If text exceeds this character limit, the font will resize to prevent overflow.";

    return (
      <>
        <div className="mod-extra-data-container">
          <div className="name-info-container">
            <span>{title}</span>
            <InfoTooltip info={infoTooltipText} />
          </div>
          <InputNumber
            className={"breakpoint-input"}
            min={1}
            max={800}
            value={
              isDynamic || (hasExperimentsEnabled && hasExperiment)
                ? change.maxChars ?? prevSetterType.current.dataKeyMaxChars
                : change.fontSize ?? prevSetterType.current.valueFontSize
            }
            onChange={(value) => {
              const cleanValue = value === null ? undefined : value;
              onChange(
                index,
                ["maxChars", "fontSize"],
                isDynamic || (hasExperimentsEnabled && hasExperiment)
                  ? [cleanValue, undefined]
                  : [undefined, cleanValue]
              );
            }}
          />
        </div>
        {valueSetterType === "value" && /\$\{.*?\}/.test(change.value || "") && (
          <span style={{ opacity: "0.5" }}>
            Font-size
            <InfoTooltip
              info={
                "Font size of the text in pixels. Available to edit if text is not dynamic"
              }
            />
            <InputNumber
              style={{ marginLeft: "0.7rem" }}
              className={"breakpoint-input"}
              min={1}
              max={800}
              disabled={true}
              value={change.fontSize}
            />
          </span>
        )}
      </>
    );
  };

  return (
    <Space direction={"vertical"} size={12} style={{ width: "100%" }}>
      <div>
        <LayerSelector
          isMulti={true}
          assetLayerPairs={props.assetLayerPairs}
          selectedAssetId={change.assetId}
          selectedLayerName={change.layerName}
          selectedLayerUid={change.layerUid}
          selectedAdditionals={change.additionals}
          onChange={props.onLayerChange}
          jsonVid={json}
          index={props.index}
          mapFilterLayerFunc={(
            p,
            layerName,
            assetId,
            layerUid,
            additionals
          ) => {
            const option = buildTextLayerOption({
              layerName,
              assetId: assetId === "main" ? undefined : assetId,
              layerUid,
              additionals,
              json,
            });
            if (option) p.push(option);
            return p;
          }}
        />
      </div>
      <div>
        <DynamicValueMod
          change={props.change}
          onChange={props.onChange}
          index={props.index}
          modId={props.modId}
          type={DynamicValueModTypes.textarea}
          jsonVid={json}
          onChangeType={setValueSetterType}
        />
      </div>
      {fontSizeOrBreakPointInput()}
      {verticalAlignmentInput}
      <div className="mod-extra-data-container">
        <div className="name-info-container">
          <span>Case Adjustment</span>
        </div>
        <Select
          value={props.change.caseAdjustment || "No change"}
          style={{ width: "100%", marginLeft: "auto" }}
          optionLabelProp="label"
          onChange={(value) =>
            props.onChange(props.index, "caseAdjustment", value ? value : "")
          }
        >
          {Object.values(CaseAdjustments).map((value) => {
            const explanation = getExplanation(value);
            const displayValue = value
              ? value.charAt(0).toUpperCase() + value.slice(1)
              : "No change";

            return (
              <Option key={value} value={value} label={displayValue}>
                <div>
                  <p style={{ margin: 0 }}>{displayValue}</p>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#cccccc",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {explanation}
                  </p>
                </div>
              </Option>
            );
          })}
        </Select>
      </div>
    </Space>
  );
}
